import React from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import { Card, rem } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';


export default function MobileDayNavigator({ events = [], onEventClick, assignableUsers = [] }) {
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const dayRef = React.useRef(null);

  // uid -> label map for assignee names
  const userLabelById = React.useMemo(() => {
    const m = new Map();
    (assignableUsers || []).forEach((u) => m.set(u.value, u.label));
    return m;
  }, [assignableUsers]);

  const eventDays = React.useMemo(() => {
    const set = new Set();
    (events || []).forEach((e) => {
      const d = new Date(e.start);
      set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    });
    return set;
  }, [events]);

  React.useEffect(() => {
    if (dayRef.current) {
      const api = dayRef.current.getApi();
      api.gotoDate(selectedDate);
    }
  }, [selectedDate]);

  const dayHeaderContent = React.useCallback((arg) => {
    const d = arg.date;
    // Align with desktop: short weekday, numeric day/month in fr
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'numeric' });
  }, []);

  return (
    <div className="fc-qt calendar-container">
      <Card withBorder shadow="sm" radius="md" p="xs" mb="xs" style={{ width: '100%' }}>
        <DatePicker
          className="mobile-date-picker"
          value={selectedDate}
          onChange={(d) => d && setSelectedDate(d)}
          size="xs"
          fullWidth
          locale="fr"
          previousIcon={<IconChevronLeft size={14} />}
          nextIcon={<IconChevronRight size={14} />}
          styles={{
            root: { width: '100%' },
            wrapper: { width: '100%' },
            calendar: { width: '100%' },
            month: { width: '100%' },
            calendarHeader: {
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: rem(6),
            },
            calendarHeaderLevel: {
              flex: 1,
              textAlign: 'center',
            },
            calendarHeaderControl: {
              width: rem(28),
              height: rem(28),
              minWidth: rem(28),
            },
          }}
          style={{ width: '100%' }}
          renderDay={(date) => {
            const has = eventDays.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
            return (
              <div>
                <span style={{ fontWeight: has ? 700 : 400 }}>{date.getDate()}</span>
              </div>
            );
          }}
        />
        
      </Card>

      <FullCalendar
        ref={dayRef}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView="timeGridDay"
        initialDate={selectedDate}
        headerToolbar={false}
        locales={[frLocale]}
        locale="fr"
        // Allow full-day scroll on mobile; start at 6am
        scrollTime={'06:00:00'}
        slotMinTime={'00:00:00'}
        slotMaxTime={'24:00:00'}
        contentHeight={600}
        dayHeaderContent={dayHeaderContent}
        // Use fixed content height so the grid scrolls
        nowIndicator
        events={events}
        eventClassNames={(arg) => {
          const assignments = Array.isArray(arg.event.extendedProps?.assignments) ? arg.event.extendedProps.assignments : [];
          return assignments.length > 0 ? ['evt-assigned'] : ['evt-unassigned'];
        }}
        eventContent={(arg) => {
          const title = arg.event.title || 'Event';
          const assignments = Array.isArray(arg.event.extendedProps?.assignments) ? arg.event.extendedProps.assignments : [];
          const names = assignments.map((a) => userLabelById.get(a.uid) || a.uid).join(', ');
          return (
            <div className="evt-content">
              <div className="evt-title">{title}</div>
              {names && <div className="evt-assignees">{names}</div>}
            </div>
          );
        }}
        eventClick={onEventClick}
      />
    </div>
  );
}
